import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { eq, or } from 'ember-truth-helpers';
import { FirebaseError } from 'firebase/app';
import { RecaptchaVerifier } from 'firebase/auth';
import { formatPhoneNumber } from 'frontend-utils/utils/phone-number-utils';
import SettingsPersonalMfaTurnOffOrUpdate from 'lumen/components/settings/personal/mfa-turn-off-or-update';
import type {
  MfaConfig,
  MfaMethodType,
} from 'lumen/utils/constants/mfa-constants';
import { MFA_MODAL } from 'lumen/utils/constants/mfa-constants';
import {
  AuthenticatorCode,
  MfaSelection,
  MfaVerificationCode,
} from 'password-form';
import {
  FIREBASE_ERROR_CODES,
  MFA_TYPES,
  MFA_VERIFICATION_CODE_LENGTH,
  type MFAType,
} from 'password-form/utils/constant-values';
import NoActiveUserError from 'password-form/utils/no-active-user-error';

interface SettingsPersonalMfaModalSignature {
  Args: {
    isMfaEnforced: boolean;
    mfaActiveMethod: MfaMethodType;
    mfaConfigs: MfaConfig[];
    onCloseModal: () => void;
    refreshMfaConfigs: () => void;
    showMfaTurnOffOrUpdateScreen: boolean;
    toggleMfaTurnOffOrUpdateScreen: () => void;
  };
}

export default class SettingsPersonalMfaModal extends Component<SettingsPersonalMfaModalSignature> {
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare firebaseService: Services['firebase-service'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare mfaService: Services['mfa-service'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked captchaCode = '';
  @tracked email = '';
  @tracked errorMessage = '';
  @tracked errorTitle = '';
  @tracked hasValidPhoneNumber = false;
  @tracked isCaptchaInVerificationCodeEnabled = false;
  @tracked isContinueForSelectionLoading = false;
  @tracked isContinueForVerificationLoading = false;
  @tracked isVerificationCodeValid = true;
  @tracked mfaType: MFAType = MFA_TYPES.MOBILE;
  @tracked next: string | undefined;
  @tracked phoneNumber = '';
  @tracked selectedCountryCode = {
    code: 'US',
    phoneCode: '1',
  };
  @tracked showAuthenticatorCode = false;
  @tracked showError = false;
  @tracked showVerificationStep = false;
  /**
   * Time-Based One-Time Password string of dynamic digits of code used to verify a MFA method
   */
  @tracked totpSecret = '';
  @tracked verificationCode = '';

  captcha: RecaptchaVerifier | null = null;
  captchaElement: HTMLDivElement | null = null;

  cleanupCaptcha = (): void => {
    this.captchaElement = null;
    this.captcha = null;
  };

  initializeRecaptcha = modifier((element: HTMLDivElement): void => {
    void this.createCaptcha(element);
  });

  get formattedPhoneNumber(): string {
    return formatPhoneNumber(
      this.selectedCountryCode.phoneCode,
      this.phoneNumber,
    );
  }

  get isContinueForSelectionDisabled(): boolean {
    if (this.mfaType === MFA_TYPES.SMS) {
      return (
        this.phoneNumber === '' ||
        this.captchaCode === '' ||
        !this.hasValidPhoneNumber
      );
    }

    return false;
  }

  get verificationValue(): string {
    if (this.mfaType === MFA_TYPES.EMAIL) {
      return this.email;
    }

    return this.formattedPhoneNumber;
  }

  constructor(owner: Owner, args: SettingsPersonalMfaModalSignature['Args']) {
    super(owner, args);

    registerDestructor(this, this.cleanupCaptcha);

    this.email = this.hotelAndMarketService.user?.email;

    // Check if the modal was opened before and set the mfaType to the last selected one
    if (this.localStorage.getItem(MFA_MODAL)) {
      this.mfaType = this.localStorage.getItem(MFA_MODAL) as MFAType;
    }

    // If the user has already an active method, we need to set the mfaType for the selection to the next one
    if (this.args.mfaActiveMethod.id === this.mfaType) {
      this.mfaType = Object.values(MFA_TYPES).find(
        (type) => type !== this.mfaType && type !== MFA_TYPES.EMAIL,
      ) as MFAType;
    }
  }

  async enrollEmail(): Promise<void> {
    try {
      this.isContinueForSelectionLoading = true;
      await this.mfaService.startEnrollGip();
      this.trackMethodSelected();
      this.showVerificationStep = true;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isContinueForSelectionLoading = false;
    }
  }

  async enrollMobile(): Promise<void> {
    try {
      this.isContinueForSelectionLoading = true;
      const totpSecret = await this.mfaService.startEnrollGip();
      if (totpSecret) {
        this.totpSecret = totpSecret.secretKey;
      }
      this.trackMethodSelected();
      this.showAuthenticatorCode = true;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isContinueForSelectionLoading = false;
    }
  }

  handleError(error: unknown): void {
    if (error instanceof FirebaseError || error instanceof NoActiveUserError) {
      if (
        error.code === FIREBASE_ERROR_CODES.REQUIRES_RECENT_LOGIN ||
        error.code === FIREBASE_ERROR_CODES.NO_ACTIVE_USER
      ) {
        this.localStorage.setItem('isLoginExpired', 'true');
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string | number | boolean'.
        const currentUrl = encodeURIComponent(this.router.currentURL);
        this.ajax
          .post('/logout/')
          .then(() => {
            window.location.href = `/login?next=${currentUrl}`;
          })
          .catch(() => {
            window.location.href = `/login?next=${currentUrl}`;
          });
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.INVALID_PHONE_NUMBER) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('error.invalid_phone_number'),
        );
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.OPERATION_NOT_ALLOWED) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('login.error.sms_not_available'),
        );
        this.errorTracking.captureException(error);
        return;
      }
      if (error.code === FIREBASE_ERROR_CODES.UNVERIFIED_EMAIL) {
        this.showErrorMessage(
          this.intl.t('general.error_title'),
          this.intl.t('login.error.unverified_email'),
        );
        this.errorTracking.captureException(error);
        return;
      }
    }
    this.showErrorMessage();
    this.errorTracking.captureException(error);
  }

  reset(): void {
    this.showVerificationStep = false;
    this.selectedCountryCode = {
      code: 'US',
      phoneCode: '1',
    };
    this.phoneNumber = '';
    this.verificationCode = '';
    this.showAuthenticatorCode = false;
    this.captchaCode = '';
  }

  trackMethodSelected(): void {
    this.usageAnalytics.track('MFA Method Selected', {
      eventTrigger: 'mfa-method-selected',
      mfaType: this.mfaType,
    });
  }

  @action
  async createCaptcha(element: HTMLDivElement | null = null): Promise<void> {
    if (element) {
      this.captchaElement = element;
    }
    if (!this.captchaElement) {
      return;
    }
    this.captcha = new RecaptchaVerifier(
      this.firebaseService.auth,
      this.captchaElement,
      {},
    );
    if (this.captcha) {
      await this.captcha.render();

      this.captchaCode = await this.captcha.verify();

      if (this.isCaptchaInVerificationCodeEnabled) {
        await this.mfaService.requestSms(
          `+${this.selectedCountryCode.phoneCode}${this.phoneNumber}`,
          this.captcha,
        );
        this.isCaptchaInVerificationCodeEnabled = false;
        // Disabling linting on this line, since this is a false negative.

        this.captcha.clear();
      }
    }
  }

  @action onBackButtonClicked(): void {
    if (this.showVerificationStep && this.showAuthenticatorCode) {
      this.showVerificationStep = false;
      return;
    }
    this.reset();
  }

  @action onCancel(): void {
    this.reset();
    this.args.onCloseModal();
  }

  @action onChangeMfaType(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.mfaType = target.value as MFAType;
  }

  @action
  async onContinueForSelectionClicked(): Promise<void> {
    if (this.isContinueForSelectionLoading) {
      return;
    }

    this.showError = false;
    // Save the selected mfaType in the local storage for if a relogin is needed
    this.localStorage.setItem(MFA_MODAL, this.mfaType);
    // Send the verification code
    switch (this.mfaType) {
      case MFA_TYPES.EMAIL: {
        await this.enrollEmail();
        break;
      }
      case MFA_TYPES.SMS: {
        try {
          if (
            this.hasValidPhoneNumber &&
            this.captchaCode !== '' &&
            this.captcha
          ) {
            this.isContinueForSelectionLoading = true;

            await this.mfaService.requestSms(
              `+${this.selectedCountryCode.phoneCode}${this.phoneNumber}`,
              this.captcha,
            );

            this.isContinueForSelectionLoading = false;
            this.trackMethodSelected();
            this.showVerificationStep = true;
          }
        } catch (error) {
          this.handleError(error);
          this.errorTracking.captureException(error);
        } finally {
          this.isContinueForSelectionLoading = false;
        }

        break;
      }
      case MFA_TYPES.MOBILE: {
        if (!this.showAuthenticatorCode) {
          await this.enrollMobile();
          return;
        }

        this.showVerificationStep = true;
        break;
      }
      default:
        this.mfaType satisfies never;
    }
  }

  @action
  async onContinueForVerificationClicked(): Promise<void> {
    if (this.isContinueForVerificationLoading) {
      return;
    }

    this.showError = false;
    if (!this.isVerificationCodeValid || !this.verificationCode) {
      return;
    }

    const isUpdate = this.args.mfaConfigs.length > 0;

    try {
      this.isContinueForVerificationLoading = true;
      // Verify the code and enroll the user
      switch (this.mfaType) {
        // TODO-MFA-EMAIL: currently we are not supporting email as a MFA method
        case MFA_TYPES.EMAIL: {
          //   await this.mfaService.verifyEmailForEnrollment(this.verificationCode.toString());
          break;
        }
        case MFA_TYPES.SMS: {
          await this.mfaService.verifySmsForEnrollment(
            this.verificationCode.toString(),
          );
          break;
        }
        case MFA_TYPES.MOBILE: {
          await this.mfaService.verifyAuthenticatorForEnrollment(
            this.verificationCode.toString(),
          );
          break;
        }
        default:
          this.mfaType satisfies never;
      }

      // Only one method must be active, so we need to unenroll before enrolling a new one
      if (this.args.mfaConfigs.length) {
        await this.unenrollMfa(false);
      }

      this.args.refreshMfaConfigs();
      this.args.onCloseModal();
      this.usageAnalytics.track('MFA Enrollment Completed', {
        eventTrigger: 'mfa-enrollment-verified',
        mfaType: this.mfaType,
        isUpdate,
      });
      this.toast.success(this.intl.t('mfa.toast.successful_addition'), {
        showIcon: true,
      });
    } catch (error) {
      this.isVerificationCodeValid = false;
      this.usageAnalytics.track('MFA Enrollment Failed', {
        eventTrigger: 'mfa-enrollment-failed',
        mfaType: this.mfaType,
      });
      this.errorTracking.captureException(error);
    } finally {
      this.isContinueForVerificationLoading = false;
    }
  }

  @action onCountryCodeChange(countryCode: {
    code: string;
    phoneCode: string;
  }): void {
    this.selectedCountryCode = countryCode;
  }

  @action onPhoneNumberChange(phoneNumber: string): void {
    this.phoneNumber = phoneNumber;
  }

  @action onResendVerificationCode(): void {
    if (this.mfaType === MFA_TYPES.SMS) {
      this.isCaptchaInVerificationCodeEnabled = true;
    }
  }

  @action setVerificationCode(code: string): void {
    this.isVerificationCodeValid = false;
    this.verificationCode = code;
    if (
      this.verificationCode.length === MFA_VERIFICATION_CODE_LENGTH ||
      this.verificationCode.length === 0
    ) {
      this.isVerificationCodeValid = true;
    }
  }

  @action showErrorMessage(
    title: string | null | undefined = this.intl.t('general.error_title'),
    message: string | null | undefined = this.intl.t('login.error.general'),
  ): void {
    this.errorMessage = '';
    this.errorTitle = '';
    if (title) {
      this.errorTitle = title;
    }
    if (message) {
      this.errorMessage = message;
    }
    this.showError = true;
  }

  @action submitVerificationCodeWithEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.onContinueForVerificationClicked();
    }
  }

  @action
  async unenrollMfa(trackDisable = true): Promise<void> {
    try {
      await this.mfaService.unenrollGip(this.args.mfaActiveMethod.id);
      this.args.refreshMfaConfigs();
      // Skip when unenrolling as part of switching methods (an update, not a disable).
      if (trackDisable) {
        this.usageAnalytics.track('MFA Disabled', {
          eventTrigger: 'mfa-disabled',
          mfaType: this.args.mfaActiveMethod.id,
        });
      }
      this.toast.success(this.intl.t('mfa.toast.successful_removal'), {
        showIcon: true,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  @action updateIsPhoneNumberValid(isValid: boolean): void {
    this.hasValidPhoneNumber = isValid;
  }

  <template>
    <PrismModal @onClose={{@onCloseModal}} @title={{t "mfa.enable"}}>
      <:default>
        <div class="mfa modal--small modal--padded">
          {{#if this.showError}}
            <div class="mb-600">
              <PrismBanner
                @bannerMessage={{this.errorMessage}}
                @bannerStyle="critical"
                @bannerTitle={{this.errorTitle}}
                @icon="warning"
                data-test-id="banner-error"
              />
            </div>
          {{/if}}
          {{#if this.showVerificationStep}}
            <MfaVerificationCode
              @initializeRecaptcha={{this.createCaptcha}}
              @isCaptchaInVerificationCodeEnabled={{this.isCaptchaInVerificationCodeEnabled}}
              @isVerificationCodeValid={{this.isVerificationCodeValid}}
              @mfaType={{this.mfaType}}
              @onResendVerificationCode={{this.onResendVerificationCode}}
              @setVerificationCode={{this.setVerificationCode}}
              @submitVerificationCodeWithEnter={{this.submitVerificationCodeWithEnter}}
              @value={{this.verificationValue}}
              @verificationCode={{this.verificationCode}}
            />
          {{else if this.showAuthenticatorCode}}
            <AuthenticatorCode
              @issuer={{this.email}}
              @qrCode={{this.totpSecret}}
            />
          {{else if @showMfaTurnOffOrUpdateScreen}}
            <SettingsPersonalMfaTurnOffOrUpdate
              @mfaActiveMethod={{@mfaActiveMethod}}
              @toggleMfaModal={{@onCloseModal}}
              @toggleMfaTurnOffOrUpdateScreen={{@toggleMfaTurnOffOrUpdateScreen}}
              @unenrollMfa={{this.unenrollMfa}}
            />
          {{else}}
            <MfaSelection
              @initializeRecaptcha={{this.createCaptcha}}
              @mfaActiveMethod={{@mfaActiveMethod.id}}
              @mfaType={{this.mfaType}}
              @onChangeMfaType={{this.onChangeMfaType}}
              @onCountryCodeChange={{this.onCountryCodeChange}}
              @onPhoneNumberChange={{this.onPhoneNumberChange}}
              @phoneNumber={{this.phoneNumber}}
              @selectedCountryCode={{this.selectedCountryCode}}
              @updateIsPhoneNumberValid={{this.updateIsPhoneNumberValid}}
            />
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div
          class="flex h-full items-center justify-between pr-500 pl-500"
          slot="modal-footer"
        >

          <div>
            {{#if (or this.showVerificationStep this.showAuthenticatorCode)}}
              <PrismButton
                @buttonType="ghost"
                @iconBefore="arrow-left"
                @label={{t "login.back"}}
                @onClick={{this.onBackButtonClicked}}
              />
            {{/if}}
          </div>

          <div class="flex flex-row gap-300">

            {{#if @showMfaTurnOffOrUpdateScreen}}
              <PrismButton
                @label={{t "general.done"}}
                @onClick={{@onCloseModal}}
              />
            {{else}}
              <PrismButton
                @buttonType="secondary"
                @label={{t "general.cancel"}}
                @onClick={{@onCloseModal}}
              />
              {{#if this.showVerificationStep}}
                <PrismButton
                  @iconAfter="arrow-right"
                  @isDisabled={{eq this.verificationCode ""}}
                  @isLoading={{this.isContinueForVerificationLoading}}
                  @label={{t "general.continue"}}
                  @onClick={{this.onContinueForVerificationClicked}}
                  data-test-id="verification-continue-button"
                />
              {{else}}
                <PrismButton
                  @iconAfter="arrow-right"
                  @isDisabled={{this.isContinueForSelectionDisabled}}
                  @isLoading={{this.isContinueForSelectionLoading}}
                  @label={{t "general.continue"}}
                  @onClick={{this.onContinueForSelectionClicked}}
                  data-test-id="selection-continue-button"
                />
              {{/if}}
            {{/if}}
          </div>

        </div>
      </:footer>
    </PrismModal>
  </template>
}
